// ADO-3749 (AC18, AC19, AC21): who is signed in, for the header's User Menu.
//
// The names come from GET /me/profile (`fetchProfile`), read once per signed-in user (the
// Auth0 `sub`) however many headers mount, and dropped when another user signs in or the
// user signs out. The response carries the stored first and last name (nullable profile
// columns) and the registry `fullName` (the display name, else first and last name). A user
// provisioned by an administrator has only a display name, so a missing first or last name
// is taken from the full name's first and last word (story DEC-3).
//
// A failed read leaves the names unknown (the menu shows its neutral "Account" chip). The next
// header to mount (the next screen) tries again; a re-render does not, as each mount asks
// once (`useSignedInUser`'s effect runs per mount and per user), so a failing endpoint is not
// hammered on every render.

import { useEffect } from 'react';
import { create } from 'zustand';
import { fetchProfile, type Profile } from '../api/profile.client';
import { useAuthStore } from '../auth/useAuthStore';
import type { UserMenuUser } from '../design-system';

export type SignedInUserStatus = 'idle' | 'loading' | 'ready' | 'error';

interface SignedInUserState {
  /** The user the names belong to (or are being read for); null when signed out. */
  readonly sub: string | null;
  readonly status: SignedInUserStatus;
  readonly user: UserMenuUser | null;
  /** Read the names for `sub` unless they are already read or being read for it (a failed
   *  read is tried again). */
  readonly load: (sub: string) => void;
  readonly reset: () => void;
}

const INITIAL = { sub: null, status: 'idle', user: null } as const;

function words(value: string | null | undefined): string[] {
  return (value ?? '')
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
}

/** The User Menu's names from a profile response; null when it names nobody. */
export function userMenuUserOf(
  profile: Pick<Profile, 'firstName' | 'lastName' | 'displayName'> & { fullName?: string },
): UserMenuUser | null {
  const storedFirst = profile.firstName?.trim() ?? '';
  const storedLast = profile.lastName?.trim() ?? '';
  const fullName =
    words(profile.fullName).join(' ') ||
    [storedFirst, storedLast].filter((part) => part !== '').join(' ') ||
    words(profile.displayName).join(' ');
  if (fullName === '') return null;
  const parts = words(fullName);
  return {
    firstName: storedFirst || (parts[0] ?? ''),
    lastName: storedLast || (parts.length > 1 ? (parts[parts.length - 1] ?? '') : ''),
    fullName,
  };
}

export const useSignedInUserStore = create<SignedInUserState>((set, get) => ({
  ...INITIAL,
  load: (sub) => {
    const state = get();
    if (state.sub === sub && (state.status === 'loading' || state.status === 'ready')) return;
    set({ sub, status: 'loading', user: null });
    fetchProfile().then(
      (profile) => {
        if (get().sub !== sub) return;
        set({ status: 'ready', user: userMenuUserOf(profile) });
      },
      () => {
        if (get().sub !== sub) return;
        set({ status: 'error', user: null });
      },
    );
  },
  reset: () => set({ ...INITIAL }),
}));

/** The signed-in user's names, or null while unknown (signed out, loading, failed). */
export function useSignedInUser(): UserMenuUser | null {
  const sub = useAuthStore((s) => (s.isAuthenticated ? (s.user?.sub ?? null) : null));
  useEffect(() => {
    const store = useSignedInUserStore.getState();
    if (sub === null) {
      if (store.sub !== null) store.reset();
      return;
    }
    store.load(sub);
  }, [sub]);
  return useSignedInUserStore((s) => (sub !== null && s.sub === sub ? s.user : null));
}
